import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

import FormGroup from '../../../../../../components/global/FormGroup';
import Alert from '../../../../../../components/global/Alert';
import Button from '../../../../../../components/global/Button';
import { getErrorString } from '../../../../../../util/normalize-errors';
import { sentryCaptureMessage } from '../../../../../../util/sentry';
import CustomControl from '../../../../../../components/global/CustomControl';
import PriceDisplay from '../../../../../../components/global/PriceDisplay';
import PromoCodeInput from '../../../../../../components/global/PromoCodeInput/PromoCodeInput';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../../../constants/account-referrals';
import useVirtualPageView from '../../../../../pro/services/analytics/useVirtualPageView';
import { CURRENCY } from '../../../../../../constants/currency';

const propTypes = {
  className: PropTypes.string,
  values: PropTypes.shape({
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  onCheckout: PropTypes.func.isRequired,
  eventItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    price: PropTypes.shape({
      value: PropTypes.number,
    }).isRequired,
  }).isRequired,
};

const CARD_ELEMENT_OPTIONS = {
  iconStyle: 'solid',
  style: {
    base: {
      iconColor: '#969ea0',
      color: '#3c4c51',
      fontWeight: 800,
      fontFamily: 'Muli, sans-serif',
      fontSize: '16px',
      fontSmoothing: 'antialiased',

      ':-webkit-autofill': {
        color: '#fce883',
      },
      '::placeholder': {
        color: '#969ea0',
        fontWeight: 800,
      },
    },
    invalid: {
      iconColor: '#ea3770',
      color: '#ea3770',
    },
  },
};

const RegistrationPayment = ({
  className,
  values,
  submitting,
  onCheckout,
  handleChange,
  eventItem,
}) => {
  const classes = classNames({
    [className]: !!className,
    'view-register-checkout-form': true,
  });

  useVirtualPageView({
    title: 'WaldoWed makes the perfect wedding gift! - Payment',
    path: '/payment',
  });

  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [stripeError, setStripeError] = useState('');
  const [price, setPrice] = useState(eventItem?.price?.value ?? 0);
  const [name, setName] = useState(
    `${values.first_name} ${values.last_name}`.trim(),
  );
  const [email, setEmail] = useState('');
  const checkPromoCodeRef = useRef();
  const [shouldCheckPromoCode, setShouldCheckPromoCode] = useState(false);
  const isFreeCheckout = price === 0;

  useEffect(() => {
    if (!values.promo_code) {
      setPrice(eventItem?.price?.value);
    }
  }, [values.promo_code]);

  const handleSubmit = async event => {
    event.preventDefault();

    if (loading || submitting) {
      return;
    }

    setLoading(true);
    setStripeError('');
    try {
      if (shouldCheckPromoCode && checkPromoCodeRef.current) {
        const success = await checkPromoCodeRef.current();
        if (!success) {
          setLoading(false);
          setStripeError('');
          return;
        }
      }

      const { error, token } = isFreeCheckout
        ? {}
        : await stripe.createToken(elements.getElement(CardElement), {
            name,
            email,
          });

      if (error) {
        throw Error(error.message);
      }

      onCheckout({
        ...values,
        email,
        stripe_token: token?.id ? token : undefined,
      });
    } catch (error) {
      setStripeError(getErrorString(error));
      sentryCaptureMessage('WEDDINGS SIGNUP - CC Capture Error ', {
        level: 'error',
        extra: {
          error,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      data-testid="gift-registration-payment"
      className={classes}
      onSubmit={handleSubmit}
    >
      <h4 className="view-register-form-title mb-2">Enter payment details</h4>
      <fieldset className="view-register-checkout-fieldset">
        <FormGroup
          label="Name"
          labelSrOnly
          id="card-name"
          name="name"
          placeholder="Name on card"
          required
          className="mb-1"
          value={name}
          onChange={event => {
            const { target } = event;
            const { value } = target;
            setName(value);
          }}
          disabled={submitting || loading}
        />

        <FormGroup
          label="Email"
          labelSrOnly
          id="card-email"
          type="email"
          name="email"
          placeholder="Your email"
          required
          value={email}
          onChange={event => {
            const { target } = event;
            const { value } = target;
            setEmail(value);
          }}
          disabled={submitting || loading}
          className="mb-1"
        />

        <PromoCodeInput
          accountReferralTypes={[
            ACCOUNT_REFERRAL_TYPE.WEDDINGS_50_USD_OFF,
            ACCOUNT_REFERRAL_TYPE.WEDDINGS_100_PC_OFF_ONCE,
            ACCOUNT_REFERRAL_TYPE.WEDDINGS,
          ]}
          labelSrOnly
          placeholder="Have a Promo Code?"
          className="view-register-promo-box"
          onPromoCodeChange={code =>
            handleChange({
              target: {
                name: 'promo_code',
                value: code,
              },
            })
          }
          onPromoCodeVerified={verifiedAccountPromoCode => {
            if (verifiedAccountPromoCode?.discounted_price) {
              setPrice(verifiedAccountPromoCode?.discounted_price?.value);
            }
          }}
          onDefaultPromoCodeParsed={() => {
            handleChange({
              target: {
                name: 'default_promo_code',
                value: '',
              },
            });
          }}
          onDismiss={() => {
            handleChange({
              target: {
                name: 'promo_code',
                value: '',
              },
            });
          }}
          defaultPromoCode={values.promo_code || values.default_promo_code}
          price={eventItem?.price?.value}
          onPromoCodeUpdate={(newPromoCode, isApplied, checkPromoCode) => {
            if (newPromoCode && !isApplied) {
              setShouldCheckPromoCode(true);
            } else {
              setShouldCheckPromoCode(false);
            }
            checkPromoCodeRef.current = checkPromoCode;
          }}
        />
      </fieldset>
      {!isFreeCheckout && (
        <fieldset className="view-register-checkout-fieldset view-register-checkout-fieldset-credit-card">
          <CardElement
            options={CARD_ELEMENT_OPTIONS}
            disabled={submitting || loading}
          />
        </fieldset>
      )}

      <CustomControl
        id="terms_checked"
        type="checkbox"
        name="terms_checked"
        label={
          <span>
            I agree to the{' '}
            <a href="/terms" target="_blank" rel="nofollow">
              Terms of Service
            </a>
          </span>
        }
        required
        onChange={handleChange}
        checked={values.terms_checked}
        className="mb-2"
      />

      {stripeError && (
        <Alert type="danger" className="mt-3">
          {stripeError}
        </Alert>
      )}

      <div className="text-center mt-2">
        <Button
          className="mt-1"
          type="submit"
          btnSize="lg"
          disabled={submitting || loading}
        >
          {submitting || loading ? (
            'Processing...'
          ) : (
            <>
              {!isFreeCheckout ? 'Purchase' : 'Get'} WaldoWed {eventItem?.name}{' '}
              For&nbsp;
              {!isFreeCheckout ? (
                <PriceDisplay price={price} currency={CURRENCY.USD} />
              ) : (
                'FREE'
              )}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

RegistrationPayment.propTypes = propTypes;

export default RegistrationPayment;
